import { useCallback, useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Pencil, Loader2, ExternalLink, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MediaPicker } from "@/components/admin/MediaPicker";

const SECTION_LABELS = {
  hero: "Hero",
  categories: "Browse by Vehicle Type",
  featured: "Featured Inventory",
  financing: "Financing",
  benefits: "Why Choose Us",
  testimonials: "Testimonials",
  contact_cta: "Contact CTA",
};

const inputCls = "bg-white border-zinc-300 text-zinc-800 focus-visible:ring-[#FF6B00] rounded-sm";

const F = ({ label, value, onChange, testid, textarea = false }) => (
  <div>
    <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}</Label>
    {textarea ? (
      <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} min-h-[90px]`} data-testid={testid} />
    ) : (
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid} />
    )}
  </div>
);

const ItemsEditor = ({ items, onChange }) => {
  const set = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="border border-zinc-200 rounded-sm p-3 space-y-2" data-testid={`benefit-edit-${i}`}>
          <div className="flex justify-between gap-2">
            <div className="flex gap-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
            </div>
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-zinc-500 hover:text-red-600" aria-label="Remove item"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
          <Input placeholder="Title" value={it.title || ""} onChange={(e) => set(i, "title", e.target.value)} className={inputCls} data-testid={`benefit-title-${i}`} />
          <Textarea placeholder="Description" value={it.description || ""} onChange={(e) => set(i, "description", e.target.value)} className={`${inputCls} min-h-[60px]`} data-testid={`benefit-desc-${i}`} />
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => onChange([...items, { title: "", description: "" }])} className="border-zinc-300 text-zinc-600 rounded-sm text-xs uppercase font-bold" data-testid="benefit-add">
        <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
      </Button>
    </div>
  );
};

const TestimonialsManager = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", role: "", quote: "" });
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(() => {
    api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/testimonials", form);
      setForm({ name: "", role: "", quote: "" });
      toast.success("Testimonial added");
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (t) => {
    await api.put(`/admin/testimonials/${t.id}`, { name: t.name, role: t.role || "", quote: t.quote, enabled: !t.enabled });
    fetchAll();
  };

  const remove = async (t) => {
    await api.delete(`/admin/testimonials/${t.id}`);
    toast.success("Testimonial deleted");
    fetchAll();
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    await api.post("/admin/testimonials/reorder", { ordered_ids: next.map((t) => t.id) });
  };

  return (
    <div className="border-t border-zinc-200 pt-4 mt-2">
      <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">Testimonials (only add real customer reviews)</p>
      <div className="space-y-2 mb-4">
        {items.map((t, i) => (
          <div key={t.id} className="flex items-center gap-2 border border-zinc-200 rounded-sm p-2.5" data-testid={`testimonial-row-${t.id}`}>
            <div className="flex flex-col">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" aria-label="Move up"><ArrowUp className="h-3 w-3" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" aria-label="Move down"><ArrowDown className="h-3 w-3" /></button>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-zinc-900 truncate">{t.name}</p>
              <p className="text-xs text-zinc-500 truncate">"{t.quote}"</p>
            </div>
            <label className="flex items-center gap-1 text-[10px] text-zinc-500">
              <input type="checkbox" checked={t.enabled !== false} onChange={() => toggle(t)} className="accent-[#FF6B00] h-3.5 w-3.5" data-testid={`testimonial-enabled-${t.id}`} />
              Show
            </label>
            <button type="button" onClick={() => remove(t)} className="text-zinc-500 hover:text-red-600" aria-label="Delete testimonial" data-testid={`testimonial-delete-${t.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-zinc-500">No testimonials yet.</p>}
      </div>
      <form onSubmit={add} className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Input required placeholder="Customer name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} data-testid="testimonial-name-input" />
          <Input placeholder="Role (optional)" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className={inputCls} data-testid="testimonial-role-input" />
        </div>
        <Textarea required placeholder="Quote" value={form.quote} onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))} className={`${inputCls} min-h-[60px]`} data-testid="testimonial-quote-input" />
        <Button type="submit" disabled={busy} variant="outline" className="border-zinc-300 text-zinc-600 rounded-sm text-xs uppercase font-bold" data-testid="testimonial-add-button">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" /> Add Testimonial</>}
        </Button>
      </form>
    </div>
  );
};

export default function AdminHomepage() {
  const [sections, setSections] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editContent, setEditContent] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const r = await api.get("/admin/content/homepage");
      setSections(r.data.data.sections || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const save = async (next) => {
    setSaving(true);
    try {
      await api.put("/admin/content/homepage", { data: { sections: next } });
      setSections(next);
      toast.success("Homepage updated. Changes are live.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      fetchAll();
    } finally {
      setSaving(false);
    }
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j], next[i]];
    save(next);
  };

  const toggle = (i) => {
    save(sections.map((s, idx) => (idx === i ? { ...s, enabled: s.enabled === false } : s)));
  };

  const openEdit = (s) => {
    setEditing(s);
    setEditContent({ ...(s.content || {}) });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const next = sections.map((s) => (s.id === editing.id ? { ...s, content: editContent } : s));
    await save(next);
    setEditing(null);
  };

  const set = (k) => (v) => setEditContent((c) => ({ ...c, [k]: v }));
  const setEv = (k) => (e) => set(k)(e.target.value);

  if (!sections) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div>;
  }

  return (
    <div data-testid="admin-homepage">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl font-black uppercase text-zinc-900">Homepage</h1>
          <p className="text-sm text-zinc-500">Edit, show/hide, and reorder homepage sections. Changes go live on save.</p>
        </div>
        <a href="/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-600 hover:border-[#FF6B00] hover:text-[#FF6B00] px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider" data-testid="homepage-preview-link">
          <ExternalLink className="h-3.5 w-3.5" /> Preview
        </a>
      </div>

      <div className="space-y-3">
        {sections.map((s, i) => (
          <div key={s.id} className="bg-white border border-zinc-200 p-4 flex flex-wrap items-center gap-4" data-testid={`section-row-${s.id}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0 || saving} className="text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" aria-label={`Move ${SECTION_LABELS[s.type]} up`} data-testid={`section-up-${s.id}`}>
                <ArrowUp className="h-4 w-4" />
              </button>
              <button onClick={() => move(i, 1)} disabled={i === sections.length - 1 || saving} className="text-zinc-500 hover:text-[#FF6B00] disabled:opacity-30" aria-label={`Move ${SECTION_LABELS[s.type]} down`} data-testid={`section-down-${s.id}`}>
                <ArrowDown className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 min-w-[160px]">
              <p className="font-bold text-zinc-900">{SECTION_LABELS[s.type] || s.type}</p>
              <p className="text-xs text-zinc-500 font-mono">{s.type}</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-zinc-500 cursor-pointer">
              <input type="checkbox" checked={s.enabled !== false} onChange={() => toggle(i)} disabled={saving} className="accent-[#FF6B00] h-4 w-4" data-testid={`section-enabled-${s.id}`} />
              Visible
            </label>
            <button onClick={() => openEdit(s)} className="p-2 border border-zinc-300 rounded-sm text-zinc-500 hover:text-[#FF6B00] hover:border-[#FF6B00]" aria-label={`Edit ${SECTION_LABELS[s.type]}`} data-testid={`section-edit-${s.id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white border-zinc-200 text-zinc-900 max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-dark" data-testid="section-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-zinc-900">Edit {SECTION_LABELS[editing?.type] || ""}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 mt-2">
            {editing?.type === "hero" && (
              <>
                <F label="Eyebrow (small text, optional)" value={editContent.eyebrow} onChange={set("eyebrow")} testid="hero-edit-eyebrow" />
                <F label="Heading *" value={editContent.heading} onChange={set("heading")} testid="hero-edit-heading" />
                <F label="Description" value={editContent.description} onChange={set("description")} textarea testid="hero-edit-description" />
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Background Image</Label>
                  <MediaPicker value={editContent.background_image} onChange={set("background_image")} testid="hero-edit-image" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <F label="Primary Button Label" value={editContent.primary_label} onChange={set("primary_label")} testid="hero-edit-primary-label" />
                  <F label="Primary Button URL" value={editContent.primary_url} onChange={set("primary_url")} testid="hero-edit-primary-url" />
                </div>
                <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
                  <input type="checkbox" checked={!!editContent.secondary_enabled} onChange={(e) => set("secondary_enabled")(e.target.checked)} className="accent-[#FF6B00] h-4 w-4" data-testid="hero-edit-secondary-enabled" />
                  Show secondary button
                </label>
                {editContent.secondary_enabled && (
                  <div className="grid grid-cols-2 gap-4">
                    <F label="Secondary Button Label" value={editContent.secondary_label} onChange={set("secondary_label")} testid="hero-edit-secondary-label" />
                    <F label="Secondary Button URL" value={editContent.secondary_url} onChange={set("secondary_url")} testid="hero-edit-secondary-url" />
                  </div>
                )}
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Text Alignment</Label>
                  <select value={editContent.alignment || "left"} onChange={setEv("alignment")} className="h-10 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm text-zinc-800" data-testid="hero-edit-alignment">
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                  </select>
                </div>
              </>
            )}

            {(editing?.type === "categories" || editing?.type === "featured" || editing?.type === "testimonials") && (
              <>
                <F label="Eyebrow" value={editContent.eyebrow} onChange={set("eyebrow")} testid="section-edit-eyebrow" />
                <F label="Heading *" value={editContent.heading} onChange={set("heading")} testid="section-edit-heading" />
              </>
            )}
            {editing?.type === "featured" && (
              <div className="grid grid-cols-2 gap-4">
                <F label="CTA Label" value={editContent.cta_label} onChange={set("cta_label")} testid="featured-edit-cta-label" />
                <F label="CTA URL" value={editContent.cta_url} onChange={set("cta_url")} testid="featured-edit-cta-url" />
              </div>
            )}

            {editing?.type === "financing" && (
              <>
                <F label="Eyebrow" value={editContent.eyebrow} onChange={set("eyebrow")} testid="financing-edit-eyebrow" />
                <F label="Heading *" value={editContent.heading} onChange={set("heading")} testid="financing-edit-heading" />
                <F label="Description" value={editContent.description} onChange={set("description")} textarea testid="financing-edit-description" />
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Bullet Points (one per line)</Label>
                  <Textarea
                    value={(editContent.bullets || []).join("\n")}
                    onChange={(e) => set("bullets")(e.target.value.split("\n").filter((s) => s.trim()))}
                    className={`${inputCls} min-h-[90px]`}
                    data-testid="financing-edit-bullets"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
                  <input type="checkbox" checked={editContent.show_calculator !== false} onChange={(e) => set("show_calculator")(e.target.checked)} className="accent-[#FF6B00] h-4 w-4" data-testid="financing-edit-calculator" />
                  Show payment calculator
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <F label="CTA Label (optional)" value={editContent.cta_label} onChange={set("cta_label")} testid="financing-edit-cta-label" />
                  <F label="CTA URL" value={editContent.cta_url} onChange={set("cta_url")} testid="financing-edit-cta-url" />
                </div>
              </>
            )}

            {editing?.type === "benefits" && (
              <>
                <F label="Eyebrow" value={editContent.eyebrow} onChange={set("eyebrow")} testid="benefits-edit-eyebrow" />
                <F label="Heading *" value={editContent.heading} onChange={set("heading")} testid="benefits-edit-heading" />
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-2">Items</Label>
                  <ItemsEditor items={editContent.items || []} onChange={set("items")} />
                </div>
              </>
            )}

            {editing?.type === "contact_cta" && (
              <>
                <F label="Heading *" value={editContent.heading} onChange={set("heading")} testid="cta-edit-heading" />
                <F label="Description" value={editContent.description} onChange={set("description")} textarea testid="cta-edit-description" />
                <div className="grid grid-cols-2 gap-4">
                  <F label="Primary Button Label" value={editContent.primary_label} onChange={set("primary_label")} testid="cta-edit-primary-label" />
                  <F label="Primary Button URL" value={editContent.primary_url} onChange={set("primary_url")} testid="cta-edit-primary-url" />
                  <F label="Secondary Button Label" value={editContent.secondary_label} onChange={set("secondary_label")} testid="cta-edit-secondary-label" />
                  <F label="Secondary Button URL" value={editContent.secondary_url} onChange={set("secondary_url")} testid="cta-edit-secondary-url" />
                  <F label="Phone" value={editContent.phone} onChange={set("phone")} testid="cta-edit-phone" />
                  <F label="Email" value={editContent.email} onChange={set("email")} testid="cta-edit-email" />
                </div>
              </>
            )}

            {editing?.type === "testimonials" && <TestimonialsManager />}

            <Button type="submit" disabled={saving} className="w-full h-11 bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm" data-testid="section-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & Publish"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
